import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle2, FileText, MessageSquare, Briefcase, Users, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { AuthContext } from '../App';

export default function HomePage() {
  const { user } = useContext(AuthContext);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="py-16 px-4" style={{ background: 'linear-gradient(135deg, #fef5f6 0%, #ffffff 50%, #f8fafc 100%)' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-5xl font-bold mb-6 leading-tight text-gray-900">
                Your CV is failing ATS.<br />
                <span className="text-[#CE1126]">Fix it before your next rejection.</span>
              </h1>
              <p className="text-xl text-gray-600 mb-4 font-medium">
                From CV to Interview, Your Trusted Career Partner.
              </p>
              <p className="text-base text-gray-600 mb-8">
                Daily UAE Jobs helps job seekers pass ATS filters, apply professionally, and prepare for interviews using AI — optimized for UAE hiring standards.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-6">
                <Link to={user ? "/cv-checker" : "/register"}>
                  <Button size="lg" className="btn-uae-red text-lg px-8 py-6" data-testid="hero-check-cv-btn">
                    Check My CV (Free)
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-2 border-gray-300">
                    Fix My CV
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-gray-500">
                <CheckCircle2 className="inline w-4 h-4 text-[#009639] mr-1" />
                1 Free CV Check • No credit card required
              </p>
            </div>
            <div className="relative">
              <Card className="p-8 shadow-2xl border-t-4 border-t-[#CE1126]">
                <h3 className="text-lg font-semibold mb-6 text-gray-700">What you get:</h3>
                <div className="space-y-5">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-[#CE1126] bg-opacity-10 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-[#CE1126]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-base">ATS CV Score</h4>
                      <p className="text-sm text-gray-600">Know if your CV passes ATS filters</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-[#009639] bg-opacity-10 flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-[#009639]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-base">Interview Booster</h4>
                      <p className="text-sm text-gray-600">Practice English interview questions</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-blue-500 bg-opacity-10 flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-blue-500" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-base">UAE Job Ready</h4>
                      <p className="text-sm text-gray-600">Optimized for Dubai, Abu Dhabi & more</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-10 bg-gray-50">
        <div className="container mx-auto px-4 text-center space-y-2">
          <div className="flex items-center justify-center space-x-2 text-gray-700">
            <Users className="w-5 h-5 text-[#009639]" />
            <p className="text-base font-medium" data-testid="trust-count-text">
              Trusted by 500+ job seekers in the UAE
            </p>
          </div>
          <p className="text-sm text-gray-500" data-testid="helped-count-text">
            We have helped 400+ job seekers secure opportunities in the UAE — free of cost.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-4">Why Daily UAE Jobs?</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Built specifically for the UAE job market. We understand local hiring standards.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 stat-card">
              <div className="w-14 h-14 rounded-full bg-[#CE1126] bg-opacity-10 flex items-center justify-center mb-4">
                <FileText className="w-7 h-7 text-[#CE1126]" />
              </div>
              <h3 className="text-xl font-bold mb-3">ATS CV Checker</h3>
              <p className="text-gray-600 mb-4">
                Get instant ATS compatibility score. Know exactly why your CV gets rejected before you apply.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>ATS score 0-100</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Keyword analysis</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Format & structure check</span>
                </li>
              </ul>
            </Card>

            <Card className="p-6 stat-card">
              <div className="w-14 h-14 rounded-full bg-[#009639] bg-opacity-10 flex items-center justify-center mb-4">
                <MessageSquare className="w-7 h-7 text-[#009639]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Interview Booster</h3>
              <p className="text-gray-600 mb-4">
                Practice English interview questions tailored for UAE employers. Build confidence before your call.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>15 curated questions</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Instant scoring</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Weak area feedback</span>
                </li>
              </ul>
            </Card>

            <Card className="p-6 stat-card">
              <div className="w-14 h-14 rounded-full bg-blue-500 bg-opacity-10 flex items-center justify-center mb-4">
                <Briefcase className="w-7 h-7 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold mb-3">CV Fix & Templates</h3>
              <p className="text-gray-600 mb-4">
                Get your CV professionally fixed by AI. Download ATS-friendly templates that work in the UAE.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>AI-powered fixes</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>UAE job market optimized</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Download ready</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Stop Getting Rejected by ATS</h2>
          <p className="text-lg text-gray-300 mb-8">
            Check your CV for free. Fix issues. Get more interview calls.
          </p>
          <Link to={user ? "/cv-checker" : "/register"}>
            <Button size="lg" className="btn-uae-red text-lg px-10 py-6" data-testid="cta-check-cv-btn">
              Check My CV (Free)
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
